export const BRAND = {
  name: "The Bermuda Co-Host",
  tagline: "Your Property. Professionally Managed. Locally Cared For.",
  subline: "Local Care. Professional Management. Exceptional Stays.",
  footerLine: "LOCALLY MANAGED. BEAUTIFULLY HOSTED.",
  phone: "+1 (441) 519-1794",
  phoneHref: "tel:+14415191794",
  email: "admin@thebermudacohost.com",
  instagram: "https://www.instagram.com/bdacohost/",
};

export const IMAGES = {
  hero: "/images/bermuda-st-georges-aerial.jpg",
  villaPool: "/images/bermuda-pool-cottages.jpg",
  livingView: "/images/bermuda-waterfront-estate.jpg",
  modernInterior: "/images/bermuda-sunset-cottages.jpg",
  bedroom: "/images/bermuda-cream-cottage.jpg",
  bathroom: "/images/bermuda-infinity-pool.jpg",
  interior: "/images/bermuda-pink-cottage.jpg",
  beach: "/images/bermuda-horseshoe-bay.jpg",
  ocean: "/images/bermuda-turquoise-coast.jpg",
  pinkSand: "/images/bermuda-horseshoe-wide.jpg",
  hamilton: "/images/bermuda-hamilton-waterfront.jpg",
  tobaccoBay: "/images/bermuda-tobacco-bay.jpg",
  moonGate: "/images/bermuda-moon-gate.jpg",
};

export const SERVICES = [
  {
    slug: "airbnb-vrbo-co-hosting",
    title: "Airbnb & VRBO Co-Hosting",
    short: "Professional guest communication, reservation management and listing support — while you keep full ownership of your listing.",
    image: IMAGES.bedroom,
    path: "/co-hosting",
    intro: "Co-hosting means you keep complete ownership and control of your Airbnb or VRBO listing — your account, your payouts, your calendar — while a local professional handles the daily work of hosting.",
    features: [
      ["Listing setup & optimization", "Compelling copy, amenity settings and photo ordering built to convert Bermuda searches."],
      ["Guest messaging", "Prompt, warm, professional responses to every guest — before, during and after their stay."],
      ["Booking inquiries", "Every inquiry answered quickly so great bookings never go cold."],
      ["Calendar management", "Availability, minimum stays and seasonal settings kept perfectly in sync."],
      ["Check-in instructions", "Clear, elegant arrival guidance so guests settle in without calling you."],
      ["Check-out communication", "Departure reminders and smooth check-out coordination."],
      ["Guest troubleshooting", "Local, on-island answers when guests need help — at any hour."],
      ["House rules", "Thoughtfully drafted rules that protect your home without scaring guests away."],
      ["Digital guest guide", "A branded guide covering the home, the neighbourhood and Bermuda itself."],
      ["Review management", "Timely guest reviews and professional responses to every review you receive."],
      ["Pricing recommendations", "Seasonal, event-aware rate guidance for the Bermuda market."],
      ["Reservation support", "Alterations, cancellations and platform issues handled on your behalf."],
    ],
    note: "You remain the listing owner at all times. We operate as your professional co-host with your explicit authorization — and you can see every message, booking and change.",
    cta: "Get a Property Assessment",
  },
  {
    slug: "full-service-property-management",
    title: "Full-Service Property Management",
    short: "Complete local oversight of your Bermuda property — guests, cleaners, inspections, maintenance and owner reporting.",
    image: IMAGES.villaPool,
    path: "/property-management",
    intro: "For owners who want one accountable local partner for everything. We become your boots on the ground in Bermuda, overseeing every operational detail of your vacation rental or second home.",
    features: [
      ["Guest management", "End-to-end handling of every stay, from inquiry to review."],
      ["Cleaner coordination", "Vetted local cleaning teams scheduled and quality-checked for every turnover."],
      ["Turnovers", "Hotel-standard changeovers timed precisely between departures and arrivals."],
      ["Property inspections", "Documented pre-arrival and post-stay inspections with photographs."],
      ["Inventory", "Tracked linens, amenities and supplies so nothing runs out mid-stay."],
      ["Supplies", "Restocking of guest essentials and household consumables."],
      ["Maintenance scheduling", "Routine and preventative upkeep planned around bookings."],
      ["Contractor access", "Approved trades let in, supervised and reported on — you never chase anyone."],
      ["Damage reporting", "Immediate documentation and communication if anything goes wrong."],
      ["Owner updates", "Clear, regular reporting so you always know the state of your home."],
      ["Property readiness", "Your home kept guest-ready and owner-ready, year-round."],
    ],
    note: "Contractor charges and materials are billed separately at cost. Significant work always requires your authorization, except under an agreed emergency procedure.",
    cta: "Let Us Manage My Property",
  },
  {
    slug: "vacation-rental-cleaning",
    title: "Vacation Rental Cleaning",
    short: "Hotel-grade turnover cleaning with linens, restocking and a final property-ready inspection after every stay.",
    image: IMAGES.bathroom,
    path: "/cleaning",
  },
  {
    slug: "property-inspections",
    title: "Property Inspections",
    short: "Scheduled inspections with photo reports for owners who travel or live overseas.",
    image: IMAGES.livingView,
    path: "/inspections",
  },
  {
    slug: "maintenance-coordination",
    title: "Maintenance Coordination",
    short: "One local contact between you and Bermuda's trusted trades — plumbing, electrical, AC and more.",
    image: IMAGES.interior,
    path: "/maintenance",
  },
  {
    slug: "guest-experience",
    title: "Guest Experience",
    short: "Concierge-level hospitality that turns a good stay into a five-star review.",
    image: IMAGES.pinkSand,
    path: "/services/guest-experience",
    intro: "Exceptional reviews are engineered, not hoped for. We design the entire guest journey — from the first message to the farewell — so your property earns the reputation it deserves.",
    features: [
      ["Warm pre-arrival communication", "Guests feel looked after before they ever land."],
      ["Digital guest guide", "House details, local secrets and island etiquette in one elegant guide."],
      ["Local recommendations", "Beaches, restaurants, taxis and experiences — curated for Bermuda."],
      ["In-stay support", "Responsive local help for anything guests need during their stay."],
      ["Welcome amenities", "Thoughtful arrival touches coordinated to your standard."],
      ["Post-stay follow-up", "Review encouragement and professional responses that build your reputation."],
    ],
    note: "Guest experience coordination is included in our management plans and available as part of co-hosting.",
    cta: "Get a Property Assessment",
  },
  {
    slug: "property-setup-onboarding",
    title: "Property Setup & Onboarding",
    short: "From empty or owner-used home to a guest-ready, bookable Bermuda vacation rental.",
    image: IMAGES.modernInterior,
    path: "/start-your-airbnb",
  },
  {
    slug: "linen-inventory-management",
    title: "Linen & Inventory Management",
    short: "Hotel-quality linens, tracked inventory and supplies that never run out mid-stay.",
    image: IMAGES.bedroom,
    path: "/services/linen-inventory-management",
    intro: "Crisp linens and fully stocked essentials are the quiet signature of a five-star stay. We manage the entire programme so every guest arrives to a home that feels freshly prepared — every single time.",
    features: [
      ["Linen programme", "Quality sheets, towels and bedding rotated, laundered and replaced on schedule."],
      ["Inventory tracking", "Counted and documented stock so nothing disappears or degrades unnoticed."],
      ["Amenity restocking", "Toiletries, kitchen staples and guest consumables kept at par."],
      ["Supply monitoring", "Proactive reordering before anything runs low."],
      ["Damage & wear reporting", "Flagged early, photographed and reported to you."],
      ["Owner closet organization", "Secure separation of owner belongings and guest inventory."],
    ],
    note: "Linen and inventory management is included in Full-Service and White-Glove management, and available as an add-on to co-hosting.",
    cta: "Get a Property Assessment",
  },
  {
    slug: "website-design",
    title: "Website Design",
    short: "Elegant, fast, direct-booking websites for Bermuda vacation rentals and hospitality businesses — yours, not a platform's.",
    image: IMAGES.livingView,
    path: "/services/website-design",
    intro: "Your property deserves more than a listing on someone else's platform. We design beautiful, mobile-first websites for Bermuda vacation rentals and hospitality businesses — so guests find you, fall in love, and book directly.",
    features: [
      ["Direct-booking websites", "Your own booking channel — fewer platform commissions, more owner control."],
      ["Property showcase design", "Photography-led pages that present your home like a boutique hotel."],
      ["Copywriting", "Benefit-led, search-aware copy written for your property and your ideal guests."],
      ["Mobile-first builds", "Most guests browse on their phone — your site will be flawless there."],
      ["Inquiry & booking forms", "Enquiries arrive organized in your inbox, ready to answer."],
      ["Search foundations", "Titles, descriptions and structured data so Bermuda searches find you."],
      ["Photography coordination", "Shot lists and professional coordination so the site launches beautifully."],
      ["Brand alignment", "Colours, typography and tone that match the experience of staying with you."],
    ],
    note: "Website design pairs naturally with our management plans — the site we build is the one we keep updated as your calendar, gallery and reviews evolve.",
    cta: "Book a Consultation",
  },
  {
    slug: "social-media-management",
    title: "Social Media Management",
    short: "Strategy, content, posting and engagement that turn your Bermuda property into an audience — and the audience into direct bookings.",
    image: IMAGES.ocean,
    path: "/services/social-media-management",
    intro: "Beautiful stays deserve to be seen. We run your property's social presence end to end, so your Bermuda rental builds a following that books directly, returns, and brings friends.",
    features: [
      ["Content strategy", "A monthly plan built around your property, the seasons and your ideal guests."],
      ["Consistent posting", "A reliable, on-brand presence — without you ever touching an app."],
      ["Property storytelling", "Stays, sunsets, details and reviews, told beautifully."],
      ["Guest content & reviews", "Guest photos and five-star moments turned into content, with permission."],
      ["Engagement & replies", "Comments and messages answered warmly and promptly."],
      ["Direct-booking funnel", "Social that points guests to your website — not just your listing."],
      ["Seasonal campaigns", "High-season pushes and off-season offers timed to Bermuda's calendar."],
      ["Performance reporting", "Simple monthly reports: what grew, what worked, what's next."],
    ],
    note: "Available standalone, or bundled with management and website services — one team, one voice, everywhere your guests look.",
    cta: "Book a Consultation",
  },
  {
    slug: "interior-design",
    title: "Interior Design & Styling",
    short: "Guest-magnetic interiors and staging — spaces that photograph beautifully, review five stars and justify premium rates.",
    image: IMAGES.interior,
    path: "/services/interior-design",
    intro: "Guests book with their eyes. We design and style Bermuda vacation rentals to photograph beautifully, live comfortably and earn their nightly rate — from single-room refreshes to full rental-ready transformations.",
    features: [
      ["Vacation-rental staging", "Spaces arranged for comfort, flow and the camera — the three things listings live on."],
      ["Photography styling", "Every room prepared and styled for the shoot that sells your property."],
      ["Furnishing plans", "Durable, beautiful, guest-proof selections sized to your budget."],
      ["Amenity & detail selection", "The finishing touches guests mention in reviews by name."],
      ["Refresh projects", "Targeted updates that lift nightly rate without a full renovation."],
      ["Sourcing coordination", "Island logistics handled — ordering, delivery, placement and setup."],
    ],
    note: "Interior design pairs with our Start Your Airbnb setup and photography coordination — a styled home earns more from day one.",
    cta: "Book a Consultation",
  },
  {
    slug: "home-cleaning",
    title: "Home Cleaning",
    short: "Regular and deep cleaning for Bermuda homes — flat rates from $50, hotel-level results, no surprise hourly bills.",
    image: IMAGES.bedroom,
    path: "/cleaning#rates",
  },
];

export const WORKFLOW_STEPS = [
  ["Booking", "A reservation comes in — we confirm, screen and prepare."],
  ["Guest Communication", "Every message answered promptly and professionally."],
  ["Pre-Arrival", "Check-in instructions, guide and arrival details sent."],
  ["Property Preparation", "Cleaned, staged, stocked and inspected."],
  ["Guest Stay", "A seamless, five-star island experience."],
  ["Local Support", "On-island help for anything, at any hour."],
  ["Check-Out", "Smooth departure and review follow-up."],
  ["Cleaning", "Hotel-grade turnover by vetted teams."],
  ["Inspection", "Documented post-stay inspection with photos."],
  ["Ready for the Next Guest", "Your home, perfect again — without you lifting a finger."],
];

export const HOW_IT_WORKS = [
  ["Consultation", "A relaxed conversation about your property, your goals and how involved you want to be."],
  ["Property Walk-Through", "We visit your home, assess its potential and note everything it needs."],
  ["Custom Management Plan", "A transparent proposal: services, pricing and exactly what we handle."],
  ["Onboarding", "Listings, photography, systems, supplies and standards — all set up for you."],
  ["We Manage Your Property", "You enjoy the income and the updates. We handle everything else."],
];

export const PRICING_TIERS = [
  {
    name: "Co-Host Essentials",
    rate: "15%",
    tagline: "of accommodation revenue",
    best: "For owners who want professional help with guests, reservations and their listing — while staying hands-on with the property itself.",
    features: ["Guest messaging & inquiries", "Reservation & calendar management", "Listing optimization", "Check-in & check-out communication", "Review management", "Pricing recommendations"],
    cta: "Get a Property Assessment",
    featured: false,
  },
  {
    name: "Full-Service Management",
    rate: "25%",
    tagline: "of accommodation revenue",
    best: "Everything in Co-Host Essentials, plus the local operational backbone: cleaning, inspections, supplies and maintenance coordination.",
    features: ["Everything in Co-Host Essentials", "Cleaning coordination & quality checks", "Property inspections with photos", "Supply & inventory monitoring", "Maintenance coordination", "Local oversight & owner updates"],
    cta: "Let Us Manage My Property",
    featured: true,
  },
  {
    name: "White-Glove Management",
    rate: "50%",
    tagline: "of accommodation revenue",
    best: "Our premium, truly hands-off service — comprehensive oversight, priority support and elevated guest experience coordination.",
    features: ["Everything in Full-Service Management", "Priority 24/7 support", "Guest-experience coordination", "Enhanced inspection schedule", "Detailed owner reporting", "Concierge-level property care"],
    cta: "Book a Consultation",
    featured: false,
  },
];

export const HOMEOWNER_PROBLEMS = [
  ["I don't have time to answer guests.", "We handle every message, inquiry and late-night question — promptly and professionally, in your voice and your interest."],
  ["I don't live in Bermuda.", "You get a trusted local partner on the ground: inspections with photos, maintenance coordination and honest updates, wherever in the world you are."],
  ["My cleaners are inconsistent.", "Our vetted teams follow a documented hotel-grade turnover checklist — and every clean is verified before the next guest arrives."],
  ["I need someone to check my property.", "Scheduled inspections with dated photo reports, so you always know your home is exactly as it should be."],
  ["I don't want guests calling me.", "They never will. Every guest touchpoint routes through us — you simply receive the updates that matter."],
  ["I don't know who to call when something breaks.", "You call one person: us. We coordinate trusted Bermudian trades, supervise access and report back."],
  ["I want to start an Airbnb but don't know where to begin.", "Our setup service takes you from property to published listing — assessment, styling guidance, photography, copy, pricing and launch."],
];

export const FAQS = [
  ["What does an Airbnb co-host do?", "A co-host manages the day-to-day work of hosting on your behalf: guest messaging, booking inquiries, calendar and pricing management, check-in instructions, troubleshooting during stays and review management. You remain the listing owner with full visibility — we simply do the work professionally."],
  ["Can you manage my property while I live overseas?", "Yes — this is one of the most common situations we support. We become your local presence in Bermuda: inspections with photo reports, cleaner and contractor coordination, guest management and regular owner updates, so you never need to be physically present."],
  ["Do I keep control of my Airbnb account?", "Absolutely. Your listing, your account, your payouts. We operate as your authorized co-host, and you can see every message and booking at any time."],
  ["Do you provide cleaning?", "Yes. We provide dedicated vacation-rental turnover cleaning — check-out cleans, kitchen and bathroom detailing, beds and linens, laundry coordination, restocking and a final property-ready inspection, with optional before-and-after photographs."],
  ["Do you handle maintenance?", "We coordinate maintenance as your single local contact: plumbing, electrical, AC, landscaping, appliances, internet, pest control and general repairs. Contractor charges are separate, and significant work always requires your authorization except under an agreed emergency procedure."],
  ["Can you help me set up a new Airbnb?", "Yes — our Start Your Airbnb service covers property assessment, vacation-rental readiness, styling and amenity recommendations, listing setup, photography coordination, copy, pricing strategy, guest guides and launch support."],
  ["Do you manage VRBO properties?", "Yes. We co-host and manage properties listed on Airbnb, VRBO, or both — keeping calendars, pricing and guest communication synchronized across platforms."],
  ["How much does vacation rental management cost?", "Our management plans start at 15%, 25% or 50% of accommodation revenue depending on the level of service. Cleaning, contractor work and materials are charged separately and transparently. Every property receives a custom written proposal before we begin."],
  ["Are cleaning fees included?", "Cleaning is charged separately from management. In most cases guests pay a cleaning fee through your listing, which offsets the cost. We provide exact cleaning quotes based on your property's size and turnover frequency."],
  ["Can you inspect my property while I am away?", "Yes. We offer scheduled property inspections with documented photo reports — ideal for owners who travel frequently or live overseas. Reports cover condition, security, weather readiness and anything requiring attention."],
  ["Can you coordinate contractors?", "Yes. We work with trusted local Bermudian trades and service providers. We arrange access, supervise the work, verify completion and report back to you with photographs."],
  ["Do you offer services without full property management?", "Yes. Every service — co-hosting, cleaning, inspections, maintenance coordination, setup — is available individually. Many owners begin with one service and expand over time."],
  ["Do I need to already have an Airbnb listing?", "No. If you have a property but have never hosted, our setup service builds everything from scratch. If you are already operating, we can take over and elevate an existing listing."],
  ["Do you provide emergency guest support?", "Yes. Guests staying at managed properties have access to local, on-island support for urgent issues — because problems don't keep office hours, and neither do we."],
];

export const BERMUDA_PARISHES = ["St. George's", "Hamilton Parish", "Smith's", "Devonshire", "Pembroke", "Paget", "Warwick", "Southampton", "Sandys", "City of Hamilton"];
